import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Pagination from '@/components/ui/pagination';
import { Search } from 'lucide-react';
import { getFilterKeyFromColumnHeader } from '@/utils/processDashboardUpdate';

/**
 * Reusable Data Table Component
 * Used by all widget components for table view
 * 
 * @param {Array} columns - Column definitions [{key, label, format, sortable}]
 * @param {Array} data - Array of row objects
 * @param {Function} onRowClick - Row-level click handler (legacy, for BarWidget/ColumnPieWidget)
 * @param {Function} onCellClick - Cell-level click handler (row, columnKey, columnLabel). When provided,
 *   cursor-pointer is applied only to cells whose header maps to a filterable filter key.
 * @param {Object} pagination - {enabled: boolean, itemsPerPage: number}
 * @param {boolean} sortable - Enable column sorting
 * @param {boolean} searchable - Enable search filtering
 * @param {string} emptyMessage - Message when no data
 */
function DataTable({
    columns = [],
    data = [],
    onRowClick,
    onCellClick,
    pagination = { enabled: true, itemsPerPage: 10 },
    sortable = true,
    searchable = true,
    emptyMessage = 'No data available'
}) {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchable || !searchTerm) return data;

        const lowerSearchTerm = searchTerm.toLowerCase();
        return data.filter(row =>
            columns.some(col => {
                const value = row[col.key];
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(lowerSearchTerm);
            })
        );
    }, [data, searchTerm, columns, searchable]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortable || !sortColumn) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];

            // Handle null/undefined
            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            // Numeric sorting
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // String sorting
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            
            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
    }, [filteredData, sortColumn, sortDirection, sortable]);

    // Paginate data
    const paginatedData = useMemo(() => {
        if (!pagination.enabled) return sortedData;

        const startIndex = (currentPage - 1) * pagination.itemsPerPage;
        const endIndex = startIndex + pagination.itemsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, pagination]);

    // Handle sort column click
    const handleSort = (columnKey) => {
        if (!sortable) return;

        if (sortColumn === columnKey) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    // Handle row click
    const handleRowClick = (row, index) => {
        if (onRowClick) {
            onRowClick(row, index);
        }
    };

    // Format cell value
    const formatCellValue = (value, format) => {
        if (value === null || value === undefined) return '-';

        if (format && typeof format === 'function') {
            return format(value);
        }

        // Default formatting for numbers with commas
        if (typeof value === 'number') {
            return value.toLocaleString();
        }

        return value;
    };

    return (
        <div className="w-full space-y-3">
            {/* Search Bar */}
            {searchable && data.length > 0 && (
                <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                        }}
                        className="pl-9"
                    />
                </div>
            )}

            {/* Table */}
            <div className="border rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHead
                                        key={column.key}
                                        className={`h-10 px-4 text-primary font-semibold ${
                                            sortable && column.sortable !== false ? 'cursor-pointer hover:bg-muted' : ''
                                        }`}
                                        onClick={() => column.sortable !== false && handleSort(column.key)}
                                    >
                                        <div className="flex items-center gap-2">
                                            {column.label}
                                            {sortColumn === column.key && (
                                                <span className="text-accent">
                                                    {sortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                            )}
                                        </div>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((row, rowIndex) => (
                                    <TableRow
                                        key={rowIndex}
                                        className={onRowClick ? 'cursor-pointer hover:bg-muted' : ''}
                                        onClick={onRowClick ? () => handleRowClick(row, rowIndex) : undefined}
                                    >
                                        {columns.map((column) => {
                                            // Per-cell click mode: only make a cell interactive if its
                                            // column header maps to a known filter key
                                            const isCellClickable = onCellClick
                                                ? !!getFilterKeyFromColumnHeader(column.label)
                                                : false;
                                            const rawValue = row[column.key];
                                            return (
                                                <TableCell
                                                    key={column.key}
                                                    title={rawValue !== null && rawValue !== undefined ? String(rawValue) : ''}
                                                    className={`py-2 px-4 text-primary${isCellClickable ? ' cursor-pointer hover:text-accent' : ''}`}
                                                    onClick={isCellClickable ? () => onCellClick(row, column.key, column.label) : undefined}
                                                >
                                                    {formatCellValue(rawValue, column.format)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="text-center py-8 text-primary/70"
                                    >
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Pagination */}
            {pagination.enabled && sortedData.length > pagination.itemsPerPage && (
                <Pagination
                    activePage={currentPage}
                    itemsCountPerPage={pagination.itemsPerPage}
                    totalItemsCount={sortedData.length}
                    onChange={setCurrentPage}
                    className="py-1.5"
                />
            )}
        </div>
    );
}

export default DataTable;
